import React, { useState, useEffect } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import styled from 'styled-components'
import axios from 'axios'
import canUseDom from 'can-use-dom'
import Cookies from 'universal-cookie'
import { Toaster } from 'react-hot-toast'
import SingleSubmissionContainer from '../../routes/single-submission/single-submission-container'
import SubmissionsAdminContainer from '../../routes/submissions-admin/submissions-admin-container'
import SubmissionsDashboardContainer from '../../routes/submissions-dashboard/submissions-dashboard-container'
import PostBuilderContainer from '../../routes/post-builder/post-builder-container'
import { ShopBuilder } from '../../routes/shop-builder/shop-builder'
import Header from '../Header'
import { adminHeaderOptions, userHeaderOptions } from '../../constants'
import { isAdmin, isDevelopment } from '../../utils'
import { hasAccessTokenCookie, refreshWpSession } from '../../config/wpSession'
import ErrorBoundary from '../../components/ErrorBoundary'
import ConfigResolver from '../../config/ConfigResolver'

const config = ConfigResolver.config()
if (canUseDom) require('./style.scss')
const cookies = new Cookies()
// The vendor-facing hashed submission link; it never needs a session.
const publicUrl = /^https:\/\/www\.stylemepretty.com\/submissions\/submission\/\d+\/.+/

const AppContainer = styled.div`
  width: 100%;
`

const App = () => {
  const [user, setUser] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const search = window.location.search
  const params = new URLSearchParams(search)
  // Token priority: Auth0 cookie, sessionStorage (legacy), dev query param
  const cookieToken = cookies.get('smp_access_token')
  const token = cookieToken || sessionStorage.getItem('token') || (isDevelopment() ? params.get('token') || config.token : '')

  useEffect(() => {
    const fetchData = async () => {
      // STY-634: the legacy sessionStorage JWT outlives the Auth0 cookie by
      // hours, but WordPress drops its session the moment the cookie goes.
      // Running on the legacy token alone leaves the app looking signed in
      // while every admin-ajax call 400s. Refresh the cookie from the Auth0
      // session first; the refresh route bounces to login if there is none.
      // The public (hashed) submission link never needs a session, and the
      // dev query-param token has no cookie to refresh.
      if (!hasAccessTokenCookie() && !isDevelopment() && !publicUrl.test(window.location.href)) {
        if (refreshWpSession()) return
        // Loop guard tripped: the cookie is not coming back. Fall through on
        // whatever token we have rather than lock the editor out entirely.
      }

      const auth = token ? { headers: {'Authorization': `Bearer ${token}`} } : ''
		  await axios
        .get(`${config.siteUrl}/api/v2/user`, auth)
        .then((result) => {
          if (result.data.data.token) sessionStorage.setItem('token', result.data.data.token)
          setUser(result.data.data)
          setIsLoading(false)
        })
        .catch(() => {
          if (publicUrl.test(window.location.href)) setIsLoading(false)
          else window.location.href = '/auth/login?returnTo=/submissions/'
        })
    }

    fetchData()
  }, [token])
	
	
  return (
    <ErrorBoundary>
      <Toaster position="bottom-right" />
      <AppContainer>
        {!isLoading && (
          <React.Fragment>
            <Header
              headerText={isAdmin(user) ? 'Submissions Admin Dashboard' : 'Submissions Dashboard'}
              headerUrl={isAdmin(user) ? '/submissions/admin' : `/submissions/dashboard/${user.email}`}
              menuItems={isAdmin(user) ? adminHeaderOptions(user) : userHeaderOptions(user)}
              image={user.avatar}
            />
            <Switch>
              <Route exact path="/submissions/admin" render={(props) => <SubmissionsAdminContainer {...props} user={user} />} />
              <Route path="/submissions/dashboard/:email" render={(props) => <SubmissionsDashboardContainer {...props} user={user} />} />
              <Route path="/submissions/user/:username" render={(props) => <Redirect to={`/submissions/dashboard/${user.email}`} />} />
              <Route path="/submissions/submission/:id/:publicHash" render={(props) => <SingleSubmissionContainer {...props} user={user} />} />
              <Route path="/submissions/submission/:id" render={(props) => <SingleSubmissionContainer {...props} user={user} />} />
              <Route path="/submissions/post-builder/:id" render={(props) => <PostBuilderContainer {...props} user={user} />} />
              <Route path="/submissions/shop-builder/:id" render={(props) => <ShopBuilder {...props} user={user} />} />
              <Route render={() => <Redirect to={isAdmin(user) ? '/submissions/admin' : `/submissions/dashboard/${user.email}`} />} />
            </Switch>
          </React.Fragment>
        )}
      </AppContainer>
    </ErrorBoundary>
  )
}

export default App